import React, { useEffect, useState } from "react";
import { InputGroup, Form } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import localization from "services/localization";
import DeleteModal from "views/Components/DeleteModal";
import ManagementHeader from "views/Components/ManagementHeader";
import CustomSelect from "views/Components/CustomSelect";
import { PromptMangmentCard } from "views/Components/PromptMangmentCard";
import AddEditPromptModal from "./AddEditPromptModal";
import { usePromptsStore } from "context/usePromptsStore";
import { usePromptCategoriesStore } from "context/usePromptCategoriesStore";
import { ALL_TYPES_OPTIONS } from "services/constants";
import SpecificAPI from "services/SpecificApi";
import { FaSearch } from "react-icons/fa";
import { useLanguageStore } from "context/useLanguageStore";

const emptyPrompt = {
  title: {},
  description: {},
  prompt_category_id: "",
  prompt_template_id: "",
  icon: "",
};
const PromptMangment = () => {
  const { currentLang } = useLanguageStore();

  const [showAddEditItem, setShowAddEditItem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(emptyPrompt);
  const [itemName, setItemName] = useState("");
  const [promptTemplates, setPromptTemplates] = useState([]);
  const { promptCategories } = usePromptCategoriesStore();
  const { prompts, setPrompts } = usePromptsStore();
  const [selectedCategory, setSelectedCategory] = useState({
    value: "all",
    label: "All Categories",
  });
  const [selectedTemplate, setSelectedTemplate] = useState({
    value: "all",
    label: "All Templates",
  });
  const [selectedType, setSelectedType] = useState({
    value: "all",
    label: localization.all || "All",
  });
  const [errors, setErrors] = useState({});

  // const getAllPromptTemplatesUnpaginated = async () => {
  //   let page = 1;
  //   let allTemplates = [];
  //   let hasMore = true;

  //   while (hasMore) {
  //     const res = await SpecificAPI.getAllPromptTemplates({
  //       page,
  //       per_page: 50,
  //     });

  //     const data = res.data || [];
  //     allTemplates = [...allTemplates, ...data];

  //     hasMore = !!res.next_page_url;
  //     page++;
  //   }

  //   return allTemplates;
  // };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    filterPrompts();
  }, [searchTerm, selectedCategory, selectedTemplate, prompts, selectedType]);

  useEffect(() => {
    setSelectedCategory({
      value: "all",
      label: localization.all_categories || "All Categories",
    });
  }, [selectedType]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const templates = await SpecificAPI.getAllPromptTemplates({});

      // Optional: sort alphabetically for better UX
      templates.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      setPromptTemplates(templates);
    } catch (err) {
      console.error("Failed to load templates:", err);
      toast.error(
        localization.failed_to_load_templates || "Failed to load templates",
      );
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (form) => {
    const e = {};

    const hasValidTitle =
      form.title &&
      Object.values(form.title).some((t) => (t || "").trim() !== "");

    if (!hasValidTitle) {
      e.title =
        localization.title_is_required ||
        "At least one title in any language is required";
    }
    const hasValidDescription =
      form.description &&
      Object.values(form.description).some((t) => (t || "").trim() !== "");

    if (!hasValidDescription) {
      e.description =
        localization.description_is_required ||
        "At least one description in any language is required";
    }

    if (!form.prompt_category_id || form.prompt_category_id === "") {
      e.prompt_category_id = "Select a prompt category";
    }

    // Template required
    if (!form.prompt_template_id || form.prompt_template_id === "") {
      e.prompt_template_id = "Select a prompt template";
    }
    return e;
  };
  const handleSubmitItem = async (data) => {
    const validationErrors = validateForm(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      setLoading(true);
      if (editingItem?.id) {
        const res = await SpecificAPI.updatePrompt({
          ...data,
          _id: editingItem.id,
        });
        setPrompts((prev) =>
          prev.map((ev) => (ev.id === editingItem.id ? res.data : ev)),
        );
      } else {
        const created = await SpecificAPI.createPrompt(data);
        setPrompts((prev) => [...prev, created.data]);
      }
      setShowAddEditItem(false);
      setEditingItem(emptyPrompt);

      toast.success(
        localization.prompt_toast_saved || "Prompt saved successfully!",
      );
    } catch (err) {
      console.error("Error saving item:", err);
      toast.error(
        err?.response?.data?.error ||
          localization.prompt_toast_failed ||
          "Failed to save prompt!",
      );
    } finally {
      setLoading(false);
    }
  };

  const filterPrompts = () => {
    let filtered = prompts;

    if (searchTerm) {
      filtered = filtered.filter((cls) =>
        cls.title?.[currentLang]
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
    }

    if (selectedCategory.value !== "all") {
      filtered = filtered.filter(
        (cls) => cls.prompt_category_id === selectedCategory.value,
      );
    }

    if (selectedTemplate.value !== "all") {
      filtered = filtered.filter(
        (cls) => cls.prompt_template_id === selectedTemplate.value,
      );
    }

    if (selectedType.value !== "all") {
      filtered = filtered.filter((prompt) => {
        const category = promptCategories.find(
          (c) => c.id === prompt.prompt_category_id,
        );

        return category?.type === selectedType.value;
      });
    }

    setFilteredItems(filtered);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowAddEditItem(true);
    setErrors({});
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setItemName(
      item.title?.[currentLang] ||
        item.title?.It ||
        item.title?.En ||
        "Unnamed Prompt",
    );
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.deletePrompt(itemToDelete.id);

      setPrompts((prev) => prev.filter((ev) => ev.id !== itemToDelete.id));

      toast.success("Prompt deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete prompt. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setEditingItem((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFormChange = (name, value) => {
    setEditingItem((prev) => {
      if (!prev) return { ...emptyPrompt, [name]: value };
      return { ...prev, [name]: value };
    });

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleCloseModal = () => {
    setShowAddEditItem(false);
    setEditingItem(emptyPrompt);
    setErrors({});
  };

  const filteredCategoriesByType =
    selectedType.value === "all"
      ? promptCategories
      : promptCategories.filter((c) => c.type === selectedType.value);

  return (
    <div className="d-flex flex-column mb-5 w-100 p-8 main-page">
      <div
        className="d-flex flex-column align-items-start mb-5 w-100"
        style={{ paddingRight: "2rem" }}
      >
        <ManagementHeader
          title={localization.all_prompts || "All Prompts"}
          subtitle={localization.manage_prompts || "Manage prompts"}
          // icon={IoTerminalOutline}
          buttonText={localization.add_prompt || "Add prompt"}
          onButtonClick={() => {
            setShowAddEditItem(true);
            setEditingItem(emptyPrompt);
            setErrors({});
          }}
          showBack={true}
          backTo="/schools"
          backLabel="Back to organization"
        />

        {/* Filters Row */}
        <div className="d-flex align-items-center gap-3 w-100 mt-4">
          {/* Search Input with Icon */}
          <InputGroup className="search-field flex-grow-1">
            <InputGroup.Text id="search-addon" className="customSearchAddon">
              <FaSearch className="icon-btn" />{" "}
            </InputGroup.Text>
            <Form.Control
              placeholder={
                localization.search_prompt || "Search by prompt name..."
              }
              aria-label="Search"
              aria-describedby="search-addon"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="customSearchInput"
            />
          </InputGroup>

          <div style={{ flex: "0 0 250px" }}>
            <CustomSelect
              options={ALL_TYPES_OPTIONS}
              value={selectedType}
              onChange={(opt) => setSelectedType(opt)}
              placeholder={localization.select_type || "Select a type"}
            />
          </div>

          {/* Prompt Category Filter */}
          <div style={{ flex: "0 0 250px" }}>
            <CustomSelect
              options={[
                {
                  value: "all",
                  label: localization.all_categories || "All Categories",
                },
                ...filteredCategoriesByType.map((c) => ({
                  value: c.id,
                  label:
                    c.title?.[currentLang] ||
                    c.title?.It ||
                    c.title?.En ||
                    localization.unnamed_category ||
                    "Unnamed Category",
                })),
              ]}
              value={selectedCategory}
              onChange={(opt) => setSelectedCategory(opt)}
              placeholder={
                localization.filter_by_category || "Filter by category"
              }
            />
          </div>

          {/* Prompt Template Filter */}
          <div style={{ flex: "0 0 250px" }}>
            <CustomSelect
              options={[
                {
                  value: "all",
                  label: localization.all_templates || "All Templates",
                },
                ...promptTemplates.map((t) => ({
                  value: t.id,
                  label:
                    t.name ||
                    localization.untitled_template ||
                    "Untitled Template",
                })),
              ]}
              value={selectedTemplate}
              onChange={(opt) => setSelectedTemplate(opt)}
              placeholder={
                localization.filter_by_template || "Filter by template"
              }
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <ClipLoader size={40} color="var(--primary-icon)" />
        </div>
      ) : (
        <div className="row g-4 w-100">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div key={item.id} className="col-6 col-md-6 col-lg-4 d-flex">
                <PromptMangmentCard
                  item={item}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  currentLang={currentLang}
                />
              </div>
            ))
          ) : (
            <p className="text-center text-muted">
              {localization.no_prompt_found || "No prompts found."}
            </p>
          )}
        </div>
      )}

      <AddEditPromptModal
        show={showAddEditItem}
        title={
          editingItem?.id
            ? localization.edit_prompt_title || "Edit Prompt"
            : localization.add_prompt_title || "Add Prompt"
        }
        formData={editingItem || emptyPrompt}
        errors={errors}
        onChange={handleChange}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitItem(editingItem);
        }}
        onClose={handleCloseModal}
        loading={loading}
        promptCategories={promptCategories}
        templates={promptTemplates}
        onFormChange={handleFormChange}
      />

      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
};

export default PromptMangment;
